import React from 'react';

// ─── Bento: the site's layout vocabulary ─────────────────────────────────────
// Everything is a tile on a grid: a rounded-3xl card with one hairline border,
// sitting on the page colour with a 16px gap. The colours come from the themed
// palette (see tailwind.config.js), so each piece below is right in both
// themes without a `dark:` variant — except where a tile is meant to CONTRAST
// with the page, which is spelled out where it happens.

/** The standard tile. `as` lets a whole tile be a link or a button. */
export function Tile({ as: Tag = 'div', className = '', children, ...props }) {
  return (
    <Tag className={`rounded-3xl border border-zinc-800 bg-zinc-900 ${className}`} {...props}>
      {children}
    </Tag>
  );
}

/**
 * The dark accent tile — near-black in BOTH themes. In dark mode it is simply
 * a tile; in light mode it is the one dark block among white ones, which is
 * what the approved light design does with the live preview.
 */
export function InkTile({ as: Tag = 'div', className = '', children, ...props }) {
  return (
    <Tag className={`rounded-3xl border border-zinc-800 bg-[#18181b] text-snow dark:bg-zinc-900 ${className}`} {...props}>
      {children}
    </Tag>
  );
}

/** The orange tile. Dark text on it in both themes: white on this orange is under 3:1. */
export function OrangeTile({ as: Tag = 'div', className = '', children, ...props }) {
  return (
    <Tag className={`rounded-3xl bg-orange-500 text-ink ${className}`} {...props}>
      {children}
    </Tag>
  );
}

/** The small pill above a headline: an orange dot and a few words. */
export function Eyebrow({ children, className = '' }) {
  return (
    <span className={`inline-flex items-center gap-2 self-start rounded-full bg-zinc-800 px-3 py-1 text-[13px] font-semibold text-zinc-300 ${className}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-orange-500" aria-hidden="true" />
      {children}
    </span>
  );
}

/** The rounded square an icon sits in at the top of a feature tile. */
export function IconSquare({ icon: Icon, className = '' }) {
  return (
    <span className={`inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-zinc-800 text-white ${className}`}>
      <Icon className="h-[22px] w-[22px]" aria-hidden="true" />
    </span>
  );
}

// ── Buttons, as class strings so they compose with <Button>, <Link> and <a> ──

/**
 * The main call to action. Orange in dark mode, a near-black pill in light
 * mode — the two approved designs, one class. Dark text on the orange: white
 * on it is under 3:1.
 */
export const BTN_PRIMARY =
  'bg-white text-zinc-950 hover:bg-zinc-200 dark:bg-orange-500 dark:text-ink dark:hover:bg-orange-500/90';

/** The inverted pill: white on dark, near-black on light. */
export const BTN_INVERT = 'bg-white text-zinc-950 hover:bg-zinc-200';

/** A quiet bordered pill for secondary actions. */
export const BTN_QUIET =
  'border border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800 hover:text-white';
